import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CalculateIcon from "@mui/icons-material/Calculate";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import OrgHeader from "./OrgHeader";
import { fetchPublicRankings } from "../features/organisation/organisationSlice";
import { getPublicEntryBreakdown } from "../features/organisation/organisationService";
import { useAccessControl } from "../context/AccessControlContext";
import { slugFromPath } from "./publicLocation";

const ROWS_PER_PAGE = 25;

// Fixed tab order: Men first, then Women, then All.
const GENDER_ORDER = ["Men", "Women", "All"];

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
}

function groupEventsByYear(events = []) {
  const byYear = new Map();

  events.forEach((event) => {
    const date = event.date ? new Date(event.date) : null;
    const year =
      date && !Number.isNaN(date.getTime()) ? date.getFullYear() : "Unknown";

    if (!byYear.has(year)) byYear.set(year, []);
    byYear.get(year).push(event);
  });

  return [...byYear.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
}

/**
 * The public ranking table for one organisation.
 *
 * Read-only by design - no flagging, no admin actions, no login. Every row
 * expands to show the events behind that player's total, which is the question
 * members actually ask when they look at a ranking.
 */
export default function OrgRankingPage() {
  const dispatch = useDispatch();
  const { organisation, rankings, isRankingsLoading, isError, notFound, message } =
    useSelector((state) => state.organisation);

  const slug = useMemo(() => slugFromPath(), []);

  const genderTabs = useMemo(() => {
    const configured = organisation?.gender_tabs;
    if (Array.isArray(configured) && configured.length > 0) {
      return GENDER_ORDER.filter((tab) => configured.includes(tab));
    }
    return GENDER_ORDER;
  }, [organisation]);

  const [genderTab, setGenderTab] = useState(null);

  // Default to the first available tab (Men for districts), and refetch when
  // the tab changes.
  useEffect(() => {
    if (genderTabs.length > 0 && !genderTab) {
      setGenderTab(genderTabs[0]);
    }
  }, [genderTabs, genderTab]);

  useEffect(() => {
    if (genderTab) {
      dispatch(fetchPublicRankings({ slug, gender: genderTab }));
    }
  }, [dispatch, slug, genderTab]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());

  // Admin-only calculation view. The public tree has no auth on custom
  // domains; useAccessControl returns a benign default there, so the icon
  // only ever appears for a logged-in admin previewing on the main domain.
  const { isAdmin } = useAccessControl();
  const [breakdown, setBreakdown] = useState({
    open: false,
    loading: false,
    error: null,
    data: null,
  });

  const openBreakdown = async (event) => {
    setBreakdown({ open: true, loading: true, error: null, data: null });

    try {
      const response = await getPublicEntryBreakdown(event.id, slug);
      setBreakdown({
        open: true,
        loading: false,
        error: null,
        data: response.data || response,
      });
    } catch (err) {
      setBreakdown({
        open: true,
        loading: false,
        error:
          err.response?.data?.message ||
          err.message ||
          "Failed to load the calculation path",
        data: null,
      });
    }
  };

  const closeBreakdown = () => {
    setBreakdown({ open: false, loading: false, error: null, data: null });
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rankings;

    return rankings.filter(
      (row) =>
        row.player_name?.toLowerCase().includes(term) ||
        String(row.bsa_number ?? "").toLowerCase().includes(term),
    );
  }, [rankings, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const toggleRow = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (notFound) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h2" gutterBottom>
          Nothing here yet
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No ranking page is configured for this address.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <OrgHeader organisation={organisation} />

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        {isError && !notFound && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {message || "Something went wrong loading the rankings."}
          </Alert>
        )}

        {genderTabs.length > 1 && (
          <Tabs
            value={genderTab || genderTabs[0]}
            onChange={(_, value) => {
              setGenderTab(value);
              setPage(1);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            {genderTabs.map((tab) => (
              <Tab key={tab} label={tab} value={tab} />
            ))}
          </Tabs>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            label="Search player or BSA number"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            sx={{ maxWidth: { sm: 320 }, width: "100%" }}
          />

          {!isRankingsLoading && (
            <Typography variant="body2" color="text.secondary">
              {filtered.length}
              {filtered.length === 1 ? " player" : " players"}
            </Typography>
          )}
        </Box>

        <Paper variant="outlined">
          {isRankingsLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading rankings...
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {rankings.length === 0
                  ? "No ranked players yet."
                  : "No players match your search."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ width: 80 }}>Rank</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visible.map((row) => {
                    const key = `${row.playerId ?? row.player_name}-${row.rank}`;
                    const isOpen = expanded.has(key);
                    const events = row.qualifying_events || [];

                    return (
                      <Fragment key={key}>
                        <TableRow hover>
                          <TableCell sx={{ py: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(key)}
                              aria-label={
                                isOpen
                                  ? `Hide events for ${row.player_name}`
                                  : `Show events for ${row.player_name}`
                              }
                            >
                              {isOpen ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>{row.rank}</TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            {row.player_name}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            {row.total_points}
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{ py: 0, borderBottom: isOpen ? undefined : "none" }}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                  Qualifying events
                                </Typography>

                                {events.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No qualifying events.
                                  </Typography>
                                ) : (
                                  groupEventsByYear(events).map(
                                    ([year, yearEvents]) => (
                                      <Box key={year} sx={{ mb: 2 }}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                          sx={{ mb: 0.5 }}
                                        >
                                          {year}
                                        </Typography>

                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Date</TableCell>
                                              <TableCell>Event</TableCell>
                                              <TableCell>Position</TableCell>
                                              <TableCell align="right">
                                                Points
                                              </TableCell>
                                              {isAdmin && <TableCell />}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {yearEvents.map((event, index) => (
                                              <TableRow
                                                key={`${event.id ?? event.name}-${index}`}
                                              >
                                                <TableCell>
                                                  {formatDate(event.date)}
                                                </TableCell>
                                                <TableCell>
                                                  {event.name}
                                                </TableCell>
                                                <TableCell>
                                                  {event.position ?? ""}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {event.points}
                                                </TableCell>
                                                {isAdmin && (
                                                  <TableCell
                                                    sx={{ py: 0, px: 1 }}
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      title="View points calculation"
                                                      onClick={() =>
                                                        openBreakdown(event)
                                                      }
                                                    >
                                                      <CalculateIcon fontSize="small" />
                                                    </IconButton>
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    ),
                                  )
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Container>

      {/* Admin-only: the calculation path behind one event's points. */}
      <Dialog
        open={breakdown.open}
        onClose={closeBreakdown}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Points Calculation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {breakdown.loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {breakdown.error && (
            <Typography variant="body2" color="error">
              {breakdown.error}
            </Typography>
          )}

          {breakdown.data && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {breakdown.data.event} ·{" "}
                {breakdown.data.event_date
                  ? formatDate(breakdown.data.event_date)
                  : "no date"}{" "}
                · stored points:{" "}
                <strong>{breakdown.data.stored_points}</strong>
              </Typography>

              <Divider />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                This organisation's path
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Base points: {breakdown.data.org.basePoints}
              </Typography>
              {breakdown.data.org.steps.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2">
                    ↓ {step.name || step.step}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      ({step.allocation ?? "missing"}%)
                    </Typography>
                  </Typography>
                </Box>
              ))}
              {breakdown.data.org.complete ? (
                <>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">Event points</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {breakdown.data.org.event_points}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">
                      Year weight (×{breakdown.data.org.weight})
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {breakdown.data.org.weighted_points}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    The weighted value is what this page displays for the
                    event.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="error">
                  One or more allocations are missing for this entry, so the
                  stored points are shown instead.
                </Typography>
              )}

              {breakdown.data.system?.breakdown?.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    System path (at the time of calculation)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Base {breakdown.data.system.basePoints} ·{" "}
                    {breakdown.data.system.breakdown
                      .map((step) => `${step.name} ${step.allocation}%`)
                      .join(" · ")}{" "}
                    · total {breakdown.data.system.totalPoints}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBreakdown}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
